import { Command } from 'cmdk'
import { useEffect, type JSX } from 'react'

import { Dialog } from '../design-system/dialog.tsx'
import { EmptyState } from '../design-system/empty-state.tsx'
import { Icon } from '../design-system/icon.tsx'
import { Panel } from '../design-system/panel.tsx'
import { cn } from '../lib/cn.ts'
import {
  isApplePlatform,
  isPageSearchShortcut,
  pageSearchScore,
  pageSearchValue
} from '../lib/page-search.ts'
import type { InternalPage } from '../lib/pages.ts'

/** One page search may offer, under the section name the rail shows. */
export interface PalettePage {
  page: InternalPage
  group: string
}

export interface PagePaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The pages this viewer may open. The caller applies viewer access. */
  pages: readonly PalettePage[]
  /** Open the page. The palette closes itself. */
  onNavigate: (path: string) => void
  /**
   * Whether the shortcut may open it. Off while viewer access is loading or
   * failed, when the rail hides search and the list would be empty.
   */
  enabled?: boolean
}

const ITEM_CLASS = cn(
  'flex cursor-pointer flex-col gap-0.5 rounded-sm px-2 py-2 text-left text-sm',
  'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground'
)

/** A group name as a `data-testid` fragment. "CS Tools" becomes "cs-tools". */
function slugifyGroup(group: string): string {
  return group.trim().toLowerCase().replace(/\s+/g, '-')
}

/**
 * Keep each rail section together, in the order the pages arrive.
 *
 * @param pages - The pages search may offer.
 * @returns Those pages grouped by the section name the rail shows.
 */
function grouped(
  pages: readonly PalettePage[]
): { group: string; pages: PalettePage[] }[] {
  const groups: { group: string; pages: PalettePage[] }[] = []
  const byGroup = new Map<string, PalettePage[]>()

  for (const entry of pages) {
    const existing = byGroup.get(entry.group)
    if (existing) {
      existing.push(entry)
      continue
    }
    const created = [entry]
    byGroup.set(entry.group, created)
    groups.push({ group: entry.group, pages: created })
  }

  return groups
}

/**
 * Page search.
 *
 * ⌘K opens it on an Apple platform, and Ctrl+K everywhere else. The rail's
 * search control opens it too. A match is the title, the group the rail
 * shows, or the description. Enter opens that page and closes the list.
 * Escape closes it.
 *
 * @param props - Page palette props.
 * @returns The dialog, mounted whether or not it is open, so the shortcut stays live.
 */
export function PagePalette({
  open,
  onOpenChange,
  pages,
  onNavigate,
  enabled = true
}: PagePaletteProps): JSX.Element {
  useEffect(() => {
    if (!enabled) return undefined
    function onKeyDown(event: KeyboardEvent): void {
      if (!isPageSearchShortcut(event, isApplePlatform())) return
      event.preventDefault()
      onOpenChange(!open)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled, onOpenChange, open])

  return (
    <Dialog
      data-testid="page-palette"
      title="Search pages"
      open={open}
      onOpenChange={onOpenChange}
    >
      <Panel
        data-testid="page-palette-panel"
        title="Search pages"
        description="Find a page by its name, its group, or its description."
      >
        <Command
          label="Search pages"
          // The panel lays its content out in a grid, and a grid item does not
          // shrink below its content. Without this, a description's unbroken
          // width widens the list past the dialog instead of truncating.
          className="min-w-0"
          // cmdk moves the highlight on Ctrl+K. That chord is this shortcut.
          vimBindings={false}
          // The default score treats a loose run of letters as a match, so a
          // word from one description also keeps unrelated pages.
          filter={pageSearchScore}
        >
          <div className="flex items-center gap-2 border-b px-3">
            <Icon name="search" className="text-muted-foreground size-4" />
            <Command.Input
              data-testid="page-palette-search"
              placeholder="Search pages"
              className="placeholder:text-muted-foreground h-9 w-full bg-transparent text-sm outline-none"
            />
          </div>
          <Command.List className="max-h-80 overflow-y-auto p-1">
            <Command.Empty>
              <EmptyState
                data-testid="page-palette-empty"
                title="No page matches"
                description="Try another word from the name, the group, or the description."
              />
            </Command.Empty>
            {grouped(pages).map(section => (
              <Command.Group
                key={section.group}
                heading={section.group}
                data-testid={`page-palette-group-${slugifyGroup(section.group)}`}
                className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium"
              >
                {section.pages.map(({ page, group }) => (
                  <Command.Item
                    key={page.id}
                    data-testid={`page-palette-item-${page.id}`}
                    value={pageSearchValue(page, group)}
                    onSelect={() => {
                      onNavigate(page.path)
                      onOpenChange(false)
                    }}
                    className={ITEM_CLASS}
                  >
                    <span className="block truncate font-medium">
                      {page.title}
                    </span>
                    <span className="block truncate text-xs opacity-80">
                      {page.description}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </Panel>
    </Dialog>
  )
}
